<template>
  <BasePopup
    ref="basePopup"
    title="Add to..."
    class="add-to-list"
    :compact="true"
    @hide="view.clearState()"
  >
    <v-wait for="Error Loading Lists">
      <template v-slot:waiting>
        <LoadingError class="add-to-list__error" />
      </template>
    </v-wait>

    <v-wait for="Loading Lists">
      <template v-slot:waiting>
        <CircleSpinner
          class="add-to-list__spinner"
          color="#ffc906"
          :size="36"
        />
      </template>

      <div class="add-to-list__list">
        <ToggleButton
          v-for="list in view.lists"
          :key="list.id"
          type="checkbox"
          class="add-to-list__input"
          :value="view.listsState[list.url_slug]"
          @update:value="(updatedValue) => view.toggleList(list, updatedValue)"
        >
          {{ list.title }}
        </ToggleButton>
      </div>

      <div class="add-to-list__controls">
        <AppButton
          class="add-to-list__btn add-to-list__btn--new"
          icon="iadd"
          :outlined="true"
          @click="showCreateList"
          >New List</AppButton
        >
        <AppButton
          class="add-to-list__btn add-to-list__btn--done"
          @click="basePopup?.hide()"
          >Done</AppButton
        >
      </div>
    </v-wait>
  </BasePopup>

  <CustomListForm ref="customListForm" :view="formView" />
</template>

<script setup lang="ts">
import { onBeforeUnmount, Raw, ref } from 'vue'

import { DocCustomListsView } from '@/models/doc.custom.lists'

import BasePopup from '@/components/ui/BasePopup.vue'
import CircleSpinner from '@/components/ui/CircleSpinner.vue'
import LoadingError from '@/components/ui/LoadingError.vue'
import ToggleButton from '@/components/ui/ToggleButton.vue'
import CustomListForm from '../custom.lists/CustomListForm.vue'
import AppButton from '@/app/components/button/AppButton.vue'
import { CreateCustomListForm } from '@/models/custom.lists'

const props = defineProps<{
  view: DocCustomListsView
}>()

const basePopup = ref<InstanceType<typeof BasePopup> | null>(null)
const customListForm = ref<InstanceType<typeof CustomListForm> | null>(null)

const formView = ref<Raw<CreateCustomListForm>>(
  new CreateCustomListForm(props.view),
)

async function show(): Promise<void> {
  if (!basePopup.value) {
    return
  }

  basePopup.value.show()
  await props.view.getLists()
}

/**
 * Show the "Create New List" popup
 * and hide this one.
 */
function showCreateList(): void {
  basePopup.value?.hide()
  customListForm.value?.show()
}

onBeforeUnmount(async () => {
  await props.view.clearState()
})

defineExpose({
  show,
})
</script>

<style lang="scss" scoped>
.add-to-list {
  --list-item-hover-bg: #{$c-light-grey};

  &__controls {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 16px;
  }

  &__btn {
    flex: 1;
  }
}

.night-mode .add-to-list {
  --list-item-hover-bg: #{$c-dark-2};
}

:deep(.add-to-list__spinner) {
  margin-top: 16px;
}

:deep(.add-to-list__list) {
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow: auto;
  padding-block: 16px;
  margin-left: -4px;
  min-height: 60px;
  max-height: 300px;

  &::-webkit-scrollbar-track-piece {
    margin-top: 16px;
  }
}

:deep(.add-to-list__input) {
  padding: 6px;
  border-radius: 4px;
  margin-block: auto;
  @include hover {
    background: var(--list-item-hover-bg);
  }
}

:deep(.add-to-list__btn) {
  width: 100%;
}

:deep(.add-to-list__btn .iconfont) {
  line-height: 1;
  margin-right: 4px;
}
</style>
